import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { AssetClassFacetService, FacetDescriptor, FacetValues } from 'src/app/core/comparable';
import { emptyFilter, FilterChange, FilterModel } from '../filter-section/filter-section.component';
import { ComparableFilterDialogComponent, ComparableFilterDialogOutput } from './comparable-filter-dialog.component';

/**
 * The comparables filter bar: a search box, the column filters behind one button, and a
 * reset — the same shape the valuation comparables table uses.
 *
 * Deliberately the same @Input/@Output contract as kt-filter-section so a list can be
 * moved across by swapping the tag. Everything it emits is applied in SQL, so the page it
 * sits on can stay server-paginated however many comparables the tenant holds.
 */
@Component({
  selector: 'kt-comparable-filter-bar',
  templateUrl: './comparable-filter-bar.component.html',
  styleUrls: ['./comparable-filter-bar.component.scss']
})
export class ComparableFilterBarComponent implements OnInit, OnDestroy {
  @Input() acType: number;
  @Input() filter$: Observable<FilterModel>;
  @Output() filterChange: EventEmitter<FilterChange> = new EventEmitter();

  filter: FilterModel = emptyFilter();
  searchControl = new FormControl('');

  descriptors: FacetDescriptor[] = [];
  values: { [key: string]: FacetValues } = {};
  loading = false;

  private _request$: Subject<void> = new Subject();
  private _onDestroy$: Subject<void> = new Subject();

  constructor(
    private dialog: MatDialog,
    private facetService: AssetClassFacetService
  ) {}

  ngOnInit(): void {
    this._watchValues();

    this.filter$.pipe(takeUntil(this._onDestroy$)).subscribe(filter => {
      this.filter = filter;
      // Keep the box in step when the page sets a filter itself (first load, reset).
      const term = filter && filter.q_name ? filter.q_name : '';
      if (this.searchControl.value !== term) {
        this.searchControl.setValue(term, { emitEvent: false });
      }
      this._requestValues();
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this._onDestroy$)
    ).subscribe(term => {
      this.filter = { ...this.filter, q_name: term };
      // A narrower search can leave the current page past the end of the results.
      this._emit(0);
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  /** How many columns currently restrict the list — shown on the Filter button. */
  get activeCount(): number {
    const counts = [this.filter.facets, this.filter.ranges, this.filter.dateParts];
    return counts.reduce((total, group) => total + (group ? Object.keys(group).length : 0), 0);
  }

  onClickFilter() {
    const ref = this.dialog.open(ComparableFilterDialogComponent, {
      width: '40rem',
      data: {
        descriptors: this.descriptors,
        values: this.values,
        facets: this.filter.facets || {},
        ranges: this.filter.ranges || {},
        dateParts: this.filter.dateParts || {}
      }
    });

    ref.afterClosed().pipe(takeUntil(this._onDestroy$)).subscribe((result: ComparableFilterDialogOutput) => {
      if (!result) {
        return;
      }
      this.filter = {
        ...this.filter,
        facets: result.facets,
        ranges: result.ranges,
        dateParts: result.dateParts
      };
      this._emit(0);
    });
  }

  onClickReset() {
    this.filter = emptyFilter();
    this.searchControl.setValue('', { emitEvent: false });
    this._emit(0);
  }

  private _emit(page: number | null) {
    this.filterChange.emit({ page, filter: this.filter });
    // Every filter narrows what the other columns can usefully offer.
    this._requestValues();
  }

  private _requestValues() {
    // getFacetValues() throws for a type with no filters defined, and a throw inside the
    // switchMap below would kill the stream for good rather than for one request.
    if (!AssetClassFacetService.supports(this.acType)) {
      return;
    }
    this.loading = true;
    this._request$.next();
  }

  private _watchValues() {
    this._request$.pipe(
      // Filters often move in bursts (typing, then a dialog); ask once for the result.
      debounceTime(300),
      // switchMap, so a response the user has already filtered past is dropped.
      switchMap(() => this.facetService.getFacetValues(this.acType, this.filter).pipe(
        // A failed lookup must not kill the stream — the next change retries. Keep the
        // values already on offer rather than blanking every column.
        catchError(() => of(null))
      )),
      takeUntil(this._onDestroy$)
    ).subscribe(response => {
      this.loading = false;
      if (!response) {
        return;
      }
      if (response.facets && response.facets.length > 0) {
        this.descriptors = response.facets;
      }
      this.values = response.data || {};
    });
  }
}
